/**
 * The gate in front of the application.
 *
 * Holds the one piece of state the cover needs to be both a door and a page you
 * can come back to: `peek`. Signed out, the gate is the way in. Signed in,
 * clicking the brand shows the same screen with a way back instead of a way
 * in — nothing stops behind it, which is why the platform keeps rendering.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LoginGate, DEV_OPEN } from "./gate/LoginGate";
import { PlatformGate } from "./gate/PlatformGate";
import { currentSession, endSession, type DemoUser } from "./auth/accounts";
import { configureIdentity, restoreUser, signOutOfPlatform, watchActivity, type PlatformUser } from "./auth/platformIdentity";
import { config } from "../config/env";
import { whenDemoLoaded } from "./domain/load";
import { useShellElement } from "./domain/domHost";
import { DemoChrome } from "./DemoChrome";
import { Farewell } from "../assistant/Farewell";
import { takeSignOff, clearSignOff, type SignOffSnapshot } from "../assistant/signOff";
import { t } from "../i18n";
import "./styles/demo.css";
/* after demo.css: it turns the demo's full-screen pages into page content */
import "./styles/embed.css";
/* and last: the surfaces the demo's own daylight palette never reached */
import "./styles/daylight.css";

interface CoverApi {
  user: DemoUser | null;
  /** Show the cover again without ending the session. */
  showCover: () => void;
  signOut: () => void;
  devMode: boolean;
}

const CoverContext = createContext<CoverApi>({
  user: null,
  showCover: () => {},
  signOut: () => {},
  devMode: DEV_OPEN,
});

export const useCover = () => useContext(CoverContext);

/*
 * A build with an identity provider signs people in there, and the platform
 * decides who they are. Without one, the in-browser gate stands, as it always
 * has for the demo.
 */
const PROVIDER = config.identity;
if (PROVIDER) configureIdentity(PROVIDER);

const asDemoUser = (u: PlatformUser): DemoUser => ({ name: u.name, role: "" });

/** Restore a session that has not idled out, so a reload does not lock you out. */
function restore(): DemoUser | null {
  const s = currentSession();
  if (!s) return null;
  return {
    name: s.email
      .split("@")[0]
      .replace(/[._]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    role: s.role,
  };
}

export function DemoGateway({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<DemoUser | null>(() => (PROVIDER ? null : restore()));
  /* Asking the provider whether this tab is already signed in takes a moment;
     the door is not shown until it answers, or a reload would flash it. */
  const [checking, setChecking] = useState(!!PROVIDER);
  const [peek, setPeek] = useState(false);
  const queryClient = useQueryClient();
  /*
   * The assistant's handover note, read at sign-out. The session is ended
   * first and this is shown afterwards — a goodbye must never be able to keep
   * someone signed in.
   */
  const [farewell, setFarewell] = useState<SignOffSnapshot | null>(null);
  /* the demo opens every detail sheet into one modal, shared by all screens */
  const modalRef = useRef<HTMLDivElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useShellElement("modal", modalRef);
  useShellElement("sheet", sheetRef);

  const api = useMemo<CoverApi>(
    () => ({
      user,
      showCover: () => setPeek(true),
      signOut: () => {
        whenDemoLoaded((d) => d.logAction("signed out", "session", null, user?.name || "", []));
        const note = takeSignOff();
        endSession();
        if (PROVIDER) {
          void signOutOfPlatform();
          /* The next person on this tab must not see the last one's answers. */
          queryClient.clear();
        }
        setUser(null);
        setPeek(false);
        clearSignOff();
        if (note) setFarewell(note);
      },
      devMode: DEV_OPEN,
    }),
    [user, queryClient],
  );

  const onEnter = useCallback((u: DemoUser) => {
    /*
     * The audit trail is what the engineering activity log reads, and every
     * entry is attributed — so who is signing in has to be recorded before the
     * action is. Without this the log opens empty on a session that plainly
     * did something.
     */
    /*
     * Written when the domain arrives rather than now. The activity log is an
     * in-session record read by one of the platform's own screens, so it is
     * always there before anybody looks at it — and signing in should not pull
     * three megabytes for a page that may never show a demo screen at all.
     */
    whenDemoLoaded((d) => {
      d.USER.name = u.name;
      d.USER.role = u.role;
      d.logAction("signed in", "session", null, u.name, []);
    });
    setUser(u);
    setPeek(false);
  }, []);

  useEffect(() => {
    if (!PROVIDER) return;
    let live = true;
    restoreUser()
      .then((u) => {
        if (live && u) onEnter(asDemoUser(u));
      })
      .catch(() => {
        /* the provider could not be reached: the door is shown, and says so when used */
      })
      .finally(() => live && setChecking(false));
    return () => {
      live = false;
    };
  }, [onEnter]);

  /*
   * Signed out after thirty minutes WITHOUT ACTIVITY, and told so. Working
   * keeps the session; a reload no longer ends it.
   */
  const [idleNote, setIdleNote] = useState("");
  const signOutRef = useRef(api.signOut);
  signOutRef.current = api.signOut;
  useEffect(() => {
    if (!PROVIDER || !user) return;
    return watchActivity(() => {
      setIdleNote(t("Signed out after 30 minutes without activity. Sign in again to carry on."));
      signOutRef.current();
    });
  }, [user]);

  /* The red strip runs the whole time development mode is on, so it cannot be
     forgotten by accident. */
  const devbar = DEV_OPEN && user && (
    <div id="devbar">
      {t("DEVELOPMENT MODE · SIGN-IN DISABLED · NOT FOR ANYBODY OUTSIDE THE BUILD · SET")} <code>DEV_OPEN = false</code>{" "}
      {t("BEFORE RELEASE")}
    </div>
  );

  const gateUp = !user || peek;

  return (
    <CoverContext.Provider value={api}>
      {devbar}
      <div id="modal" ref={modalRef} data-i18n-dom>
        <div className="sheet" id="sheet" ref={sheetRef}></div>
      </div>
      {/* The platform keeps rendering behind the cover — that is the point of a peek. */}
      {user && children}
      {user && <DemoChrome />}
      {gateUp && PROVIDER && !checking && (
        <PlatformGate
          peek={peek}
          notice={idleNote}
          onEnter={(u) => {
            setIdleNote("");
            onEnter(asDemoUser(u));
          }}
          onBack={() => setPeek(false)}
          onSignOut={api.signOut}
        />
      )}
      {gateUp && !PROVIDER && (
        <LoginGate
          peek={peek}
          onEnter={onEnter}
          onBack={() => setPeek(false)}
          onSignOut={api.signOut}
          /* The audit trail exists from the start of the session and is empty
             until something is done — which is "caution", not "none". */
          auditCount={0}
        />
      )}
      {/* After the gate, never instead of it: the session has already ended. */}
      {farewell && <Farewell snapshot={farewell} onClose={() => setFarewell(null)} />}
    </CoverContext.Provider>
  );
}
